import { toast } from "sonner";
import { call, put, takeLatest, takeEvery } from "redux-saga/effects";
import {
  createConsultation as createConsultationService,
  deleteConsultation as deleteConsultationService,
  getConsultationById as getConsultationByIdService,
  getConsultationsByPatient as getConsultationsByPatientService
} from "../../services/consultations";
import * as types from "./types";
import { adaptResponseError } from "../../utils/utils";

/**
 * Sagas du domaine Consultation.
 *
 * <p>Conventions :
 * <ul>
 *   <li>Tous les appels HTTP passent par {@code call(service, ...)}.</li>
 *   <li>Les services renvoient l'enveloppe apiRequest
 *       {@code {ok, status, data, message}}.</li>
 *   <li>POST /consultation ne retourne qu'un Long (id) — on chaîne un
 *       getConsultationByIdService pour récupérer le DTO complet.</li>
 *   <li>On rafraîchit la liste du patient si {@code payload.patientId}
 *       est présent dans le payload de création.</li>
 * </ul>
 */

function* createConsultation({ payload }) {
  try {
    const result = yield call(createConsultationService, payload);
    if (!result?.ok) {
      yield put({
        type: types.CREATE_CONSULTATION_FAILURE,
        payload: adaptResponseError(result || {})
      });
      return;
    }

    const newId = result.data;
    let createdConsultation = { id: newId };

    if (newId != null) {
      const detail = yield call(getConsultationByIdService, newId);
      if (detail?.ok) {
        createdConsultation = detail.data;
      }
    }

    toast.success("Consultation enregistrée avec succès");
    yield put({
      type: types.CREATE_CONSULTATION_SUCCESS,
      payload: createdConsultation
    });

    const patientId = payload?.patientId;
    if (patientId != null) {
      yield put({
        type: types.GET_CONSULTATIONS_BY_PATIENT_REQUEST,
        payload: { patientId }
      });
    }
  } catch (error) {
    console.error("CreateConsultationSagaErr: ", error);
    toast.error("Erreur inattendue lors de la création de la consultation");
    yield put({
      type: types.CREATE_CONSULTATION_FAILURE,
      payload: [{ message: error?.message || "Erreur inattendue" }]
    });
  }
}

function* deleteConsultation({ payload }) {
  try {
    const id = payload?.id;
    if (id == null) {
      yield put({
        type: types.DELETE_CONSULTATION_FAILURE,
        payload: [{ message: "id manquant" }]
      });
      return;
    }
    const result = yield call(deleteConsultationService, id);
    if (!result?.ok) {
      yield put({
        type: types.DELETE_CONSULTATION_FAILURE,
        payload: adaptResponseError(result || {})
      });
      return;
    }
    toast.success("Consultation supprimée");
    yield put({ type: types.DELETE_CONSULTATION_SUCCESS, payload: { id } });
  } catch (error) {
    console.error("DeleteConsultationSagaErr: ", error);
    yield put({
      type: types.DELETE_CONSULTATION_FAILURE,
      payload: [{ message: error?.message || "Erreur inattendue" }]
    });
  }
}

function* getConsultationById({ payload }) {
  try {
    const id = payload?.id;
    if (id == null) {
      yield put({
        type: types.GET_CONSULTATION_BY_ID_FAILURE,
        payload: [{ message: "id manquant" }]
      });
      return;
    }
    const result = yield call(getConsultationByIdService, id);
    if (!result?.ok) {
      yield put({
        type: types.GET_CONSULTATION_BY_ID_FAILURE,
        payload: adaptResponseError(result || {})
      });
      return;
    }
    yield put({
      type: types.GET_CONSULTATION_BY_ID_SUCCESS,
      payload: result.data
    });
  } catch (error) {
    console.error("GetConsultationByIdSagaErr: ", error);
    yield put({
      type: types.GET_CONSULTATION_BY_ID_FAILURE,
      payload: [{ message: error?.message || "Erreur inattendue" }]
    });
  }
}

/**
 * Liste plate des consultations d'un patient. Le back retourne directement
 * un tableau {@code List<ConsultationResponse>}.
 */
function* getConsultationsByPatient({ payload }) {
  try {
    const { patientId } = payload || {};
    if (patientId == null) {
      yield put({
        type: types.GET_CONSULTATIONS_BY_PATIENT_FAILURE,
        payload: [{ message: "patientId manquant" }]
      });
      return;
    }
    const result = yield call(getConsultationsByPatientService, patientId);
    if (!result?.ok) {
      yield put({
        type: types.GET_CONSULTATIONS_BY_PATIENT_FAILURE,
        payload: adaptResponseError(result || {})
      });
      return;
    }
    yield put({
      type: types.GET_CONSULTATIONS_BY_PATIENT_SUCCESS,
      payload: Array.isArray(result.data) ? result.data : []
    });
  } catch (error) {
    console.error("GetConsultationsByPatientSagaErr: ", error);
    yield put({
      type: types.GET_CONSULTATIONS_BY_PATIENT_FAILURE,
      payload: [{ message: error?.message || "Erreur inattendue" }]
    });
  }
}

export default function* ConsultationsSaga() {
  yield takeLatest(types.CREATE_CONSULTATION_REQUEST, createConsultation);
  yield takeLatest(types.DELETE_CONSULTATION_REQUEST, deleteConsultation);
  yield takeEvery(types.GET_CONSULTATION_BY_ID_REQUEST, getConsultationById);
  yield takeLatest(
    types.GET_CONSULTATIONS_BY_PATIENT_REQUEST,
    getConsultationsByPatient
  );
}
